import {
  signOut,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { auth, db, localDb } from './config';
import { doc, setDoc, getDoc, serverTimestamp, collection, writeBatch } from 'firebase/firestore';
import { businessPresets } from '../utils/businessPresets';
import { registerUserForApp } from '../utils/centralRegistration';

// Email/password authentication is LOCALHOST-ONLY (dev/QA).
// Production uses OAuth providers (Google or Microsoft) only.
// LoginForm gates the UI behind a hostname check so this never ships.
export const signInWithEmailPassword = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (e) {
    return { user: null, error: e?.message || 'Login failed' };
  }
};

// Sign in with Google (basic auth only)
// For OAuth integrations (email, calendar), use ApiService.connectOAuth('google') after login
// Optional `signupContext` carries UTM / landing-page attribution for new signups.
export const signInWithGoogle = async (signupContext = null) => {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    // Try popup first, fallback to redirect for CORS issues
    try {
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Register user with central system (triggers onboarding + welcome email)
      try {
        console.log('📝 Registering user for booking-system...');
        await registerUserForApp(user, 'booking-system', {
          initialPlan: signupContext?.plan || 'free',
          registrationContext: signupContext?.registrationContext,
        });
        console.log('✅ User registered for booking-system');
      } catch (regError) {
        console.warn('⚠️ Registration failed (non-blocking):', regError);
      }

      // Note: OAuth tokens are NOT stored locally
      // For email/calendar integrations, user must separately call:
      // ApiService.connectOAuth('google') from Settings > Integrations
      // This uses the centralized OAuth system which stores tokens securely server-side

      // ✅ PATTERN A: User documents created by BACKEND on first API call
      // Backend uses Admin SDK which bypasses Firestore security rules
      // This prevents race conditions and privilege escalation issues
      // See: functions/src/middleware/ensureUserDocument.ts

      return {
        user,
        error: null
      };
    } catch (popupError) {
      if (popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.message.includes('CORS')) {
        // Fallback to redirect
        await signInWithRedirect(auth, provider);
        return { user: null, error: 'redirect_initiated' };
      } else {
        throw popupError;
      }
    }
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Sign in with Microsoft (basic auth only)
// For OAuth integrations (email, calendar), use ApiService.connectOAuth('microsoft') after login
// Optional `signupContext` carries UTM / landing-page attribution for new signups.
export const signInWithMicrosoft = async (signupContext = null) => {
  try {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      tenant: 'common',
      prompt: 'select_account'
    });

    // Try popup first, fallback to redirect for CORS issues
    try {
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Register user with central system (triggers onboarding + welcome email)
      try {
        console.log('📝 Registering user for booking-system...');
        await registerUserForApp(user, 'booking-system', {
          initialPlan: signupContext?.plan || 'free',
          registrationContext: signupContext?.registrationContext,
        });
        console.log('✅ User registered for booking-system');
      } catch (regError) {
        console.warn('⚠️ Registration failed (non-blocking):', regError);
      }

      // Note: OAuth tokens are NOT stored locally
      // For email/calendar integrations, user must separately call:
      // ApiService.connectOAuth('microsoft') from Settings > Integrations
      // This uses the centralized OAuth system which stores tokens securely server-side

      // ✅ PATTERN A: User documents created by BACKEND on first API call
      // Backend uses Admin SDK which bypasses Firestore security rules
      // This prevents race conditions and privilege escalation issues
      // See: functions/src/middleware/ensureUserDocument.ts

      return {
        user,
        error: null
      };
    } catch (popupError) {
      if (popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.message.includes('CORS')) {
        // Fallback to redirect
        await signInWithRedirect(auth, provider);
        return { user: null, error: 'redirect_initiated' };
      } else {
        throw popupError;
      }
    }
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Clear all browser storage and cache
const clearAllStorage = () => {
  try {
    // Clear localStorage
    localStorage.clear();
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear IndexedDB (used by Firebase and other services)
    if ('indexedDB' in window) {
      // Get all databases and clear them
      indexedDB.databases?.().then(databases => {
        databases.forEach(db => {
          if (db.name) {
            const deleteReq = indexedDB.deleteDatabase(db.name);
            deleteReq.onerror = () => console.warn(`Could not delete IndexedDB: ${db.name}`);
          }
        });
      }).catch(() => {
        // Fallback for browsers that don't support indexedDB.databases()
        console.warn('Could not enumerate IndexedDB databases');
      });
    }
    
    // Clear service worker caches
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
      }).catch(() => {
        console.warn('Could not clear service worker caches');
      });
    }
    
    // Clear cookies (domain-specific)
    document.cookie.split(";").forEach(cookie => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      // Clear for current domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      // Clear for parent domain
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      // Clear for root domain
      const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
      if (rootDomain !== window.location.hostname) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${rootDomain}`;
      }
    });
    
    console.log('✅ All browser storage and cache cleared');
  } catch (error) {
    console.warn('⚠️ Some storage clearing failed:', error);
  }
};

// Sign out with comprehensive cleanup
export const logout = async () => {
  try {
    // First sign out from Firebase
    await signOut(auth);
    
    // Then clear all browser storage and cache
    clearAllStorage();
    
    // Force reload to ensure clean state (optional, but recommended)
    setTimeout(() => {
      window.location.href = '/';
    }, 100);
    
    return { error: null };
  } catch (error) {
    // Even if Firebase signout fails, still clear storage for security
    clearAllStorage();
    return { error: error.message };
  }
};

// Password reset functionality removed for production
// Use OAuth providers (Google or Microsoft) for authentication

// ❌ DEPRECATED: User documents now created by BACKEND (Dec 31, 2025)
// This function is no longer called from frontend to prevent:
// 1. Race conditions with multiple simultaneous document creations
// 2. Firestore security rule violations (can't set privileged fields like 'role')
// 3. Privilege escalation attacks
// Backend creates user documents on first API call using Admin SDK
// See: functions/src/middleware/ensureUserDocument.ts
const createUserDocument = async (user, additionalData = {}) => {
  if (!user) return;

  // ✅ CORRECT: Creates user documents in CENTRAL Firestore (paymentgateway-ab783)
  // 'db' is initialized with centralApp in config.js
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    const { displayName, email, photoURL } = user;
    const { businessType, ...restData } = additionalData;

    try {
      const userData = {
        displayName,
        email,
        photoURL,
        createdAt: serverTimestamp(),
        // ⚠️ NOTE: 'role' is a privileged field blocked by Firestore rules
        // Backend will set role during onboarding or first API call
        themePreference: 'dark-maximalist', // default theme for new users
        ...restData,
      };

      if (businessType && businessPresets[businessType]) {
        const preset = businessPresets[businessType];
        userData.businessHours = preset.businessHours;
        userData.bookingRules = preset.bookingRules;
        userData.customFields = preset.customFields;
      }

      await setDoc(userRef, userData);

      if (businessType && businessPresets[businessType]) {
        const preset = businessPresets[businessType];
        const servicesCollectionRef = collection(userRef, 'services');
        const batch = writeBatch(db);

        preset.serviceTemplates.forEach(service => {
          const newServiceRef = doc(servicesCollectionRef);
          batch.set(newServiceRef, service);
        });

        await batch.commit();
      }
    } catch (error) {
      console.error('Error creating user document:', error);
    }
  }

  return userRef;
};

// ✅ PATTERN A COMPLIANCE - User documents fetched from CENTRAL Firestore
// This function fetches user documents from paymentgateway-ab783 (central project)
// The 'db' export from config.js points to centralApp Firestore
export const getUserDocument = async (uid) => {
  if (!uid) return null;

  try {
    // ✅ CORRECT: Fetches from CENTRAL Firestore (paymentgateway-ab783)
    // 'db' is initialized with centralApp in config.js
    const userDoc = await getDoc(doc(db, 'users', uid));
    return userDoc.exists() ? { uid, ...userDoc.data() } : null;
  } catch (error) {
    console.error('Error fetching user document:', error);
    return null;
  }
};

// Handle redirect result (for OAuth fallback)
export const handleRedirectResult = async () => {
  try {
    const result = await getRedirectResult(auth);
    if (result && result.user) {
      // ✅ PATTERN A: User documents created by BACKEND on first API call
      // No need to create user document here - backend handles it
      return { user: result.user, error: null };
    }
    return { user: null, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Load OAuth tokens from Firestore
export const loadOAuthTokensFromFirestore = async (userId) => {
  if (!userId) return null;

  const loadedTokens = { google: null, microsoft: null };

  try {
    // ⚠️ OAuth tokens are managed by CENTRAL API
    // They are stored in: integrations/{userId}/connections/{provider}
    // Frontend should NOT read tokens directly - use ApiService.getMyIntegrations()

    // Try to load Google tokens (stored in CENTRAL Firestore - integrations)
    const googleIntegrationDoc = await getDoc(doc(db, 'integrations', userId, 'connections', 'google'));

    if (googleIntegrationDoc.exists()) {
      const data = googleIntegrationDoc.data();

      // Restore to localStorage for fast access
      if (data.accessToken) {
        localStorage.setItem('google_access_token', data.accessToken);
        console.log('✅ Google access token restored from Firestore');
      }
      if (data.refreshToken) {
        localStorage.setItem('google_refresh_token', data.refreshToken);
        console.log('✅ Google refresh token restored from Firestore');
      }
      localStorage.setItem('oauth_provider', 'google');

      loadedTokens.google = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        provider: 'google',
        expiresAt: data.expiresAt
      };
    }

    // Try to load Microsoft tokens (stored in CENTRAL Firestore - integrations)
    const microsoftIntegrationDoc = await getDoc(doc(db, 'integrations', userId, 'connections', 'microsoft'));

    if (microsoftIntegrationDoc.exists()) {
      const data = microsoftIntegrationDoc.data();

      // Restore to localStorage for fast access
      if (data.accessToken) {
        localStorage.setItem('microsoft_access_token', data.accessToken);
        console.log('✅ Microsoft access token restored from Firestore');
      }
      if (data.refreshToken) {
        localStorage.setItem('microsoft_refresh_token', data.refreshToken);
        console.log('✅ Microsoft refresh token restored from Firestore');
      }
      if (!loadedTokens.google) {
        localStorage.setItem('oauth_provider', 'microsoft');
      }

      loadedTokens.microsoft = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        provider: 'microsoft',
        expiresAt: data.expiresAt
      };
    }
  } catch (error) {
    // ℹ️ This is expected if user hasn't connected OAuth yet
    // Don't show error for "permission-denied" as it just means no connection exists
    if (error.code !== 'permission-denied') {
      console.error('Error loading OAuth tokens from Firestore:', error);
    }
  }

  return loadedTokens;
};

// Auth state observer with automatic token restoration
export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      // Automatically restore OAuth tokens from Firestore on login
      await loadOAuthTokensFromFirestore(user.uid);

      // ✅ PATTERN A: Clean implementation
      // Backend uses verifyMultiProjectToken() to accept tokens from central auth
      // No need to sign into local auth - backend handles cross-project verification
    }
    callback(user);
  });
};
